import { useFormikContext } from "formik";
import React, { FC, useEffect, useState } from "react";
import { CustomFieldBuilder } from "../../containers/utilities/CustomFieldBuilder";
import { useLocale } from "../../contexts/localeContext";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { ListQuery } from "../../models/ListQuery";
import { Name } from "../../models/Name";
import { PaginationModel } from "../../models/Pagination";
import PresetCustomField from "../../models/PresetCustomField";
import { getPresetActionNamesWithPagination } from "../../services/presetActionService";
import { getPresetInputNamesWithPagination } from "../../services/presetInputService";
import { getPresetParameterNamesWithPagination } from "../../services/presetParameterService";
import { getPresetSettingById, getPresetTypeNameById } from "../../services/presetService";
import { appendFilter } from "../../utilities/filters";
import { requiredValidation } from "../../utilities/Validation";
import Label from "../label/label";
import PaginatedSearchableSelectField from "../selection-field/paginated-searchable-select-field";
import { SelectType } from '../settingFields/SettingSelectField';
import { SettingTextField } from '../settingFields/SettingTextField';
import {getWizardContentDescription} from "../../utilities/WizardContentUtil";

interface WizardContentFormProps {
    onNext?: (event: any) => void | undefined;
    onBack?: (event: any) => void | undefined;
    itemId?: string;
    systemProfileId?: string;
    supportedFields?: any[];
    typeTemplateId?: string;
    presetTemplateType?: SelectType;
    currentItem?: PresetCustomField;
    templateName: string;
    setTypeTemplateId?: (typeTemplateId: string) => void;
    setTemplateName?: (templateName: string) => void;
    parentProfileId?: string;
}

export const WizardContentForm: FC<WizardContentFormProps> = ({ onBack = undefined, onNext = undefined, itemId = undefined, systemProfileId = '', supportedFields = undefined, typeTemplateId = undefined, presetTemplateType = SelectType.presetInputTemplate, currentItem, templateName, setTypeTemplateId, setTemplateName, parentProfileId = '' }) => {
    const nameAccessor = 'name';
    const formik = useFormikContext();
    const [hasError, setHasError] = useState(false);
    const { translate } = useLocale();
    const customFieldBuilder: CustomFieldBuilder = new CustomFieldBuilder(formik, translate);

    useEffect(() => {
        formik.validateForm();
    }, [(formik.values as any).custom])

    useEffect(() => {
        // get supported field system name that is used as accessors
        const supportedFieldAccessors = supportedFields?.map(field => field.systemName) ?? new Array(0);

        // for the first page, the name is required.
        if (formik.errors[nameAccessor]
            // for the second page, any supported field errors should block it.
            || (supportedFieldAccessors.length > 0 && Object.keys(formik.errors as any).length > 0)) {
            setHasError(true)
        }
        else {
            setHasError(false)
        }
    }, [formik.errors])

    const onTemplateSelected = async (value) => {
        formik.setFieldValue('typeId', value);
        try {
            const preset: PresetCustomField = await getPresetSettingById(value, translate);
            if (preset) {
                formik.setFieldValue('typeId', value)
                formik.setFieldValue('profileId', parentProfileId);
                formik.setFieldValue('sdist', preset.sdist);
                formik.setFieldValue('settingIdToCopy', preset.settingIdToCopy ?? value)
                if (setTypeTemplateId) {
                    setTypeTemplateId(preset.typeId?.toString() ?? '');
                }
                if (setTemplateName) {
                    const presetTypeName = await getPresetTypeNameById(preset.typeId?.toString() ?? '');
                    setTemplateName(presetTypeName);
                }
            }
        } catch (error) {
            console.log(error); //TODO: update to show proper error message
        }
    }

    const handleBack = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        if (onBack) {
            onBack(e);
        }
    };

    const handleNext = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        if (onNext && (itemId || typeTemplateId)) {
            if (!hasError) {
                let description = ((formik.values as any)['name']) + '\n';
                let detailDescription = getWizardContentDescription(formik.values, supportedFields);
                formik.validateForm().then(() => onNext({ itemName: description, presetItem: formik.values,
                    detailDescription: detailDescription.detailDescription, toolTipDescription: detailDescription.tooltipDescription
                    , showToolTip: detailDescription.showToolTip }));
            }
        }
    }

    const createFields = () => {
        const DOSId = 2003;
        var arrayFields = [] as any[];
        if (currentItem?.sdist == 'GLOBAL' && currentItem?.typeId == DOSId) {
            supportedFields?.filter(field => {
                if (!field.systemName.includes('I_DOS_CONTACT_LA')) {
                    arrayFields.push(field);
                }
            });
        }
        else {
            arrayFields = supportedFields as any;
        }

        return arrayFields?.reduce((formFields, field) =>
            customFieldBuilder.buildFormField(formFields, field, false, true, undefined),
            new Array(0))
            .map(fieldBundle =>
                fieldBundle.map(field => field.content)
            )
    }

    const getTemplateDataMethod = () => {
        switch (presetTemplateType) {
            case SelectType.presetInputTemplate:
                return (listParameter?: ListQuery) => getPresetInputNamesWithPagination({ ...listParameter, filters: appendFilter(systemProfileId, 'Profile', 'profile.id', appendFilter('I_*', 'Setting Type', 'settingType.stSysname', [])) });
            case SelectType.presetActionTemplate:
                return (listParameter?: ListQuery) => getPresetActionNamesWithPagination({ ...listParameter, filters: appendFilter(systemProfileId, 'Profile', 'profile.id', appendFilter('ACT_*', 'Setting Type', 'settingType.stSysname', [])) });
            case SelectType.presetParameterTemplate:
                return (listParameters) => getPresetParameterNamesWithPagination({ ...listParameters, filters: appendFilter(systemProfileId, 'Profile', 'profile.id', appendFilter('P_*', 'Setting Type', 'settingType.stSysname', [])) });
            default:
                return (listParameter?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => new Promise((resolve, reject) => {
                    resolve({
                        rows: new Array(0),
                        pagination: {
                            maxPageSize: 5,
                            pageNumber: 1,
                            totalCount: 0,
                            totalPages: 1,
                            sizes: [5, 50, 100],
                            pages: [1]
                        }
                    });
                })
        }
    }

    return <div className="flex flex-col pt-3 pr-3 h-full relative">
        <div className="flex flex-col overflow-y-auto" style={{ height: '85%' }}>
            {supportedFields
                // supported fields are passed in for the second wizard screen
                ? <div className="flex flex-col pt-3">
                    {createFields()}
                </div>
                // or the name field and potentially the template picker is shown in the first wizard screen
                : <><div className="flex flex-row pt-3">
                    <SettingTextField
                        accessor={nameAccessor}
                        required validation={(value) => requiredValidation(value, translate)}
                        autoFocus={true}
                        displayName={translate.byKey(TranslationKeys.displayName)}></SettingTextField>
                </div>
                    {itemId // check if a record is being edited, or a record has been chose to copy from.
                        ? null
                        : <div className="flex flex-col flex-1 pr-6">
                            <div className="flex flex-row justify-between" id='typeLabelId'>
                                <Label
                                    required
                                    htmlFor='typeId'
                                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.type)}:</Label>
                                {formik.errors['typeId'] ? <span className='text-red-600 text-xs'>{formik.errors['typeId']}</span> : null}
                            </div>
                            <PaginatedSearchableSelectField
                                name={translate.byKey(TranslationKeys.type)}
                                key={'typeId'}
                                accessor={'typeId'}
                                dataMethod={getTemplateDataMethod()}
                                searchAccessor={'trnName'}
                                required={true}
                                onSelect={(value) => onTemplateSelected(value)} />
                        </div>}
                </>}
        </div>
        <div className="flex flex-row justify-end absolute bottom-0 right-0 pr-3 pb-3 ">
            <div className="flex flex-row mt-4">
                <button
                    className={`secondary secondary-inverse py-1 px-5 ${onBack ? '' : 'invisible'}`}
                    type="button"
                    onClick={handleBack}>{translate.byKey(TranslationKeys.back)}</button>
            </div>
            <div className="flex flex-row justify-end mt-4 pl-3">
                <button
                    className={`py-1 px-5 secondary  ${hasError || !(itemId || typeTemplateId) ? 'secondary-disabled cursor-not-allowed' : 'secondary-enabled'}`}
                    type='button'
                    onClick={handleNext}>{translate.byKey(TranslationKeys.next)}</button>
            </div>
        </div>
    </div>;
};
